import React, { useMemo } from 'react';

import {
  getCurrentFieldValue,
  getFieldDisplayName,
  isFieldRequired,
  isFieldValueMissing,
} from '@/components/treatment/v3/request/package-edit/utils/fieldValidation';
import type { ReadTemplateField } from '@/services/api/generated/templatesV2Service';

import { useGetTemplate } from '../hooks/useGetTemplate';
import { TemplatePreview } from './TemplatePreview';

export type BaseTemplateField = ReadTemplateField;

interface TemplateFieldEditorProps {
  templateId: string;
  matterContext: Record<string, string | undefined>;
  overrides: Record<string, string>;
  setOverride: (fieldId: string, value: string) => void;
  inline?: boolean;
}
export const TemplateFieldEditor: React.FC<TemplateFieldEditorProps> = ({
  templateId,
  matterContext,
  overrides,
  setOverride,
  inline = false,
}) => {
  const { template } = useGetTemplate(templateId);

  const filteredFields = useMemo(() => {
    return (
      template?.fields?.filter(
        (field) => field.field_type === 'TEXT' || field.field_type === 'DATE',
      ) ?? []
    );
  }, [template]);

  const getCurrentValue = (field: BaseTemplateField): string => {
    return getCurrentFieldValue(field, matterContext, overrides);
  };

  const getDisplayName = (field: BaseTemplateField): string => {
    return getFieldDisplayName(field);
  };

  const handleInputChange =
    (fieldId: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      setOverride(fieldId, value);
    };

  const containerClassName = inline
    ? ''
    : 'shrink-0 max-h-[70vh] overflow-y-auto border rounded-lg p-6 bg-white shadow';

  return (
    <div className={containerClassName}>
      {filteredFields.length === 0 ? (
        <p
          className={inline ? 'text-xs text-gray-500' : 'text-sm text-gray-500'}
        >
          No fillable fields in this template.
        </p>
      ) : (
        <div className={inline ? 'space-y-2' : 'space-y-4'}>
          {filteredFields.map((field) => {
            const value = getCurrentValue(field);
            const label = getDisplayName(field);
            const required = isFieldRequired(field);
            const missing = required && isFieldValueMissing(value);
            const isFromMatterContext = !!field.context_key;

            return (
              <div key={field.id} className="flex flex-col">
                <label
                  htmlFor={field.id}
                  className={`${inline ? 'text-[11px]' : 'text-sm'} font-medium mb-1 ${
                    missing ? 'text-red-700' : 'text-gray-700'
                  }`}
                >
                  {label}
                  {required && <span className="text-red-500 ml-1">*</span>}
                  {isFromMatterContext && (
                    <span
                      className={`${inline ? 'text-[9px]' : 'text-xs'} text-blue-600 ml-1.5`}
                    >
                      (from matter)
                    </span>
                  )}
                </label>
                <input
                  id={field.id}
                  type="text"
                  value={value}
                  onChange={handleInputChange(field.id)}
                  placeholder="Enter value"
                  className={`border rounded ${inline ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} focus:outline-none focus:ring-2 w-full ${
                    missing
                      ? 'border-red-300 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {missing && (
                  <p
                    className={`${inline ? 'text-[9px]' : 'text-xs'} text-red-600 mt-0.5`}
                  >
                    This field is required
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface TemplateInstantiatorProps {
  templateId: string;
  matterContext: Record<string, string | undefined>;
  overrides: Record<string, string>;
  setOverride: (fieldId: string, value: string) => void;
}

export const TemplateInstantiator: React.FC<TemplateInstantiatorProps> = ({
  templateId,
  matterContext,
  overrides,
  setOverride,
}) => {
  const { template, isLoading, error } = useGetTemplate(templateId);

  const filteredFields = useMemo(() => {
    return (
      template?.fields?.filter(
        (field) => field.field_type === 'TEXT' || field.field_type === 'DATE',
      ) ?? []
    );
  }, [template]);

  const getCurrentValue = (field: BaseTemplateField): string => {
    return getCurrentFieldValue(field, matterContext, overrides);
  };

  const getDisplayName = (field: BaseTemplateField): string => {
    return getFieldDisplayName(field);
  };

  const handleInputChange =
    (fieldId: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      setOverride(fieldId, value);
    };

  if (isLoading){
    return <div>Loading…</div>;
  }

  if (error || !template) {
    return <div>Error fetching template</div>;
  }

  return (
    <div className="flex gap-6 items-start w-full">
      {/* Left pane – field overrides */}
      <div className="w-80 shrink-0 max-h-[70vh] overflow-y-auto border rounded-lg p-6 bg-white shadow">
        <h2 className="text-lg font-semibold mb-4">Fields</h2>
        {filteredFields.length === 0 ? (
          <p className="text-sm text-gray-500">
            No fillable fields in this template.
          </p>
        ) : (
          <div className="space-y-4">
            {filteredFields.map((field) => {
              const value = getCurrentValue(field);
              const label = getDisplayName(field);
              const required = isFieldRequired(field);
              const missing = required && isFieldValueMissing(value);
              const isFromMatterContext = !!field.context_key;

              return (
                <div key={field.id} className="flex flex-col">
                  <label
                    htmlFor={field.id}
                    className={`text-sm font-medium mb-1 ${
                      missing ? 'text-red-700' : 'text-gray-700'
                    }`}
                  >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                    {isFromMatterContext && (
                      <span className="text-xs text-blue-600 ml-2">
                        (from matter)
                      </span>
                    )}
                  </label>
                  <input
                    id={field.id}
                    type="text"
                    value={value}
                    onChange={handleInputChange(field.id)}
                    placeholder="Enter value"
                    className={`border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 w-full ${
                      missing
                        ? 'border-red-300 focus:ring-red-500 bg-red-50'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  {missing && (
                    <p className="text-xs text-red-600 mt-1">
                      This field is required and cannot be empty
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Right pane – live preview */}
      <div className="flex-1 w-[1000px]">
        <TemplatePreview
          templateId={templateId}
          matterContext={matterContext}
          overrides={overrides}
        />
      </div>
    </div>
  );
};
